import formatLegacyWebDateTimeString from "util/formatLegacyWebDateTimeString";

export const MANAGEMENT_ACTION_ADMIT = "ADMIT";
export const MANAGEMENT_ACTION_PAY = "PAY";

export const UPLOAD_ACTIVITY_ID = "echeq.manage.files.upload";
export const SEND_ACTIVITY_ADMIT = "mass.echeq.manage.admit.send";
export const SEND_ACTIVITY_PAY = "mass.echeq.manage.pay.send";

/** Cantidad fija de ECHEQs por página en modales de detalle (confirmación y ticket). */
export const ECHEQ_MASS_MANAGE_DETAIL_ROWS_PER_PAGE = 10;

/** Alcance de mass.echeq.manage.detail — MODAL: overlay liviano; BATCH: página de detalle. */
export const DETAIL_SCOPE_MODAL = "MODAL";
export const DETAIL_SCOPE_BATCH = "BATCH";

export function getActionValue(actionField) {
    if (actionField == null) {
        return null;
    }
    if (typeof actionField === "object" && actionField.value != null) {
        return String(actionField.value);
    }
    return String(actionField);
}

export function resolveSendActivityId(actionValue) {
    return actionValue === MANAGEMENT_ACTION_PAY ? SEND_ACTIVITY_PAY : SEND_ACTIVITY_ADMIT;
}

export function resolveDownloadExampleConfigKey(actionValue) {
    return actionValue === MANAGEMENT_ACTION_PAY
        ? "frontend.massiveEcheqPay.file.download.example"
        : "frontend.massiveEcheqAdmit.file.download.example";
}

export function resolveUploadParams(formValues = {}) {
    const actionValue = getActionValue(formValues.action);
    if (!actionValue) {
        return {};
    }

    // RootResource.files.upload no propaga campos custom; la acción viaja en description (ADMIT|PAY).
    return {
        description: actionValue,
    };
}

export function validateUploadActionRequired(formValues = {}) {
    if (getActionValue(formValues.action)) {
        return undefined;
    }
    return "echeq.massive.manage.action.required";
}

export function mapValidDetailRows(details = [], validLinesCount) {
    const normalized = (Array.isArray(details) ? details : [])
        .map((row, index) => {
            const beneficiaryDocument = String(row.beneficiaryDocument ?? row.documento_beneficiario ?? "").trim();
            const lineReference = String(row.lineReference ?? row.referencia ?? "").trim();

            return {
                id: row.lineNumber ?? row.id ?? index + 1,
                lineNumber: row.lineNumber ?? row.id ?? index + 1,
                echeqId: String(row.echeqId ?? row.echeqID ?? row.value ?? "").trim(),
                ...(beneficiaryDocument ? { beneficiaryDocument } : {}),
                ...(lineReference ? { lineReference } : {}),
            };
        })
        .filter((row) => row.echeqId.length > 0);

    if (typeof validLinesCount === "number" && validLinesCount >= 0 && normalized.length !== validLinesCount) {
        return normalized.slice(0, validLinesCount);
    }

    return normalized;
}

export function mapFormValuesToPreviewPayload(values = {}) {
    const actionValue = getActionValue(values.action);
    const file = values.file || {};

    return {
        managementAction: actionValue,
        reference: String(values.reference ?? "").trim(),
        idFile: file.idFile,
        fileName: file.fileName || file.newFileName,
        validLines: file.validLines,
        invalidLines: file.invalidLines,
        idRelatedFile: file.idRelatedFile,
        nameRelatedFile: file.nameRelatedFile,
        totalAmount: file.totalAmount,
        details: mapValidDetailRows(file.details, file.validLines),
    };
}

export function mapPreviewPayloadToFormValues(formData = {}, actionOptions = []) {
    if (!formData?.managementAction) {
        return null;
    }

    const action = actionOptions.find((option) => String(option.value) === String(formData.managementAction)) || {
        value: formData.managementAction,
        label: formData.managementAction,
    };

    const validLines = Number(formData.validLines ?? 0);
    const invalidLines = Number(formData.invalidLines ?? 0);

    return {
        action,
        reference: formData.reference ?? "",
        file: {
            idFile: formData.idFile != null ? String(formData.idFile) : undefined,
            idRelatedFile: formData.idRelatedFile != null ? String(formData.idRelatedFile) : undefined,
            fileName: formData.fileName,
            newFileName: formData.fileName,
            nameRelatedFile: formData.nameRelatedFile,
            validLines,
            invalidLines,
            totalLines: validLines + invalidLines,
            totalAmount: formData.totalAmount,
            details: formData.details || [],
        },
    };
}

export function mapPreviewPayloadToSummary(payload = {}) {
    const action = payload.managementAction?.toLowerCase();
    const summary = {
        action,
        reference: payload.reference,
        processDate: new Date().toISOString(),
        fileName: payload.fileName,
        echeqsQuantity: payload.validLines || 0,
        idFile: payload.idFile,
        invalidLines: payload.invalidLines,
        idRelatedFile: payload.idRelatedFile,
        nameRelatedFile: payload.nameRelatedFile,
        managementAction: payload.managementAction,
        rawTotalAmount: payload.totalAmount,
        details: mapValidDetailRows(payload.details, payload.validLines),
    };

    return summary;
}

export function mapSummaryToTicketDisplaySummary(summary = {}) {
    return {
        action: summary.action,
        reference: summary.reference,
        processDate: formatLegacyWebDateTimeString(summary.processDate, false),
        fileName: summary.fileName,
        echeqsQuantity: summary.echeqsQuantity || 0,
    };
}

/** Summary del ticket desde `transaction.data` (incluye `processDate` persistido en send). */
export function mapTransactionDataToTicketDisplaySummary(transactionData) {
    const data = transactionData?.data || {};

    return mapSummaryToTicketDisplaySummary({
        action: data.managementAction?.toLowerCase(),
        reference: data.reference,
        processDate: data.processDate,
        fileName: data.fileName,
        echeqsQuantity: data.validLines,
    });
}

export function mapSummaryToSendPayload(summary = {}, otp) {
    return {
        _otp: otp,
        managementAction: summary.managementAction,
        reference: summary.reference,
        idFile: summary.idFile,
        fileName: summary.fileName,
        validLines: summary.echeqsQuantity,
        invalidLines: summary.invalidLines,
        idRelatedFile: summary.idRelatedFile,
        nameRelatedFile: summary.nameRelatedFile,
        totalAmount: summary.rawTotalAmount,
    };
}

export function resolveSendActivityPath(summary = {}) {
    return `/${resolveSendActivityId(summary.managementAction)}`;
}
